import { TagPanelView } from '../view/TagPanelView';
import { templateTagPanel } from '../view/template';
import { TagPanelRepository } from '../repository/TagPanelRepository';
import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { TagPanelWebComponent } from '..';
import { showToast } from '../../helpers/commonFunctions';
import { OnTagPanelClose, OnTagPanelMaybeClose } from '../../common/events/TagPanelEvents';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { TagActionContext } from '../../common/types/TagActionContext';
import { TagData } from '../../types/tag-data';

export class TagPanelController extends BaseWebComponentController {
  public component: TagPanelWebComponent;
  public dataRepository: TagPanelRepository;
  public view: TagPanelView;
  public viewConstructor: typeof TagPanelView;
  public dataRepositoryConstructor: typeof TagPanelRepository;

  constructor(component: TagPanelWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: TagPanelView,
      viewTemplate: templateTagPanel,
      dataRepositoryConstructor: TagPanelRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.emitMaybeCloseTagWindowEvent,
      },
      {
        element: this.view.dom.$saveButton,
        event: 'click',
        callback: this.saveTagHandler,
      },
    ];
  }

  async getDataForView(): Promise<{ context: TagActionContext | null; tagData: TagData | null }> {
    let context, tagData;
    try {
      context = this.component.context;
    } catch (e) {
      context = null;
    }

    if (context === TagActionContext.Edit) {
      tagData = await this.dataRepository.getTagById(this.component.editedTagId);
    } else if (context === TagActionContext.Clone) {
      tagData = await this.dataRepository.getTagById(this.component.clonedTagId, true);
    } else {
      tagData = null;
    }

    return {
      context: context,
      tagData: tagData,
    };
  }

  private emitCloseTagWindowEvent() {
    this.component.dispatchEvent(new OnTagPanelClose());
  }

  private emitMaybeCloseTagWindowEvent() {
    this.component.dispatchEvent(new OnTagPanelMaybeClose());
  }

  private async saveTagHandler() {
    const context = this.component.context;
    if (!TagActionContext.isValid(this.component.context)) {
      throw new Error(`[TagPanel] invalid context: ${context}`);
    }

    // show spinner
    this.view.dom.$saveButton.showSpinner = true;
    this.view.dom.$saveButton.disabled = true;

    let saveTagResult, successToast, errorToast;

    // error is handled in data provider
    if (context === TagActionContext.Create || context === TagActionContext.Clone) {
      saveTagResult = await this.createTagRecord();
      successToast = this.dependencies.translateFn('Tag created successfully.');
      if (saveTagResult.errorMessage) {
        errorToast = saveTagResult.errorMessage;
      } else {
        errorToast = this.dependencies.translateFn('Failed to create tag.');
      }
    } else if (context === TagActionContext.Edit) {
      saveTagResult = await this.updateTagRecord();
      successToast = this.dependencies.translateFn('Tag updated successfully.');
      errorToast = this.dependencies.translateFn('Failed to update tag.');
    }

    if (saveTagResult.success) {
      showToast(ToastSeverity.SUCCESS, successToast);
    } else {
      showToast(ToastSeverity.ERROR, errorToast);
    }

    // remove the spinner
    this.view.dom.$saveButton.showSpinner = false;
    this.view.dom.$saveButton.disabled = false;

    if (saveTagResult.success) {
      this.emitCloseTagWindowEvent();
    }
  }

  private async createTagRecord(): Promise<{ success: boolean; errorMessage?: string }> {
    this.view.dom.$tagDetailsForm.controller.view.tagRulesFormGroup.removeFormChild(['rulesValidatorHelper']);
    const tagRecord = this.view.dom.$tagDetailsForm.value;
    if (this.component.context === TagActionContext.Clone) {
      tagRecord['clonedFrom'] = {
        name: this.component.clonedTagName,
        id: this.component.clonedTagId,
        companyName: this.component.clonedTagCompanyName,
      }
    }
    return await this.dataRepository.addTag(tagRecord);
  }

  private async updateTagRecord(): Promise<{ success: boolean }> {
    this.view.dom.$tagDetailsForm.controller.view.tagRulesFormGroup.removeFormChild(['rulesValidatorHelper']);
    const tagRecord = this.view.dom.$tagDetailsForm.value;
    tagRecord['_id'] = this.component.editedTagId;
    return await this.dataRepository.updateTag(tagRecord);
  }
}
